import React, { useState, useEffect } from 'react';
import { useAxios } from '../../core/_axios';
import LoadingSpinner from '../../components/LoadingSpinner';
import { Link } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { usePageHeader } from '../../contexts/PageHeaderContext';
import usePageTitle from '../../hooks/usePageTitle';

interface TranscriptionLog {
  id: number;
  signature_id: number;
  signature_unique_identifier: string;
  service_name: string | null;
  service_unique_identifier: string | null;
  account_name: string | null;
  model_used: string;
  prompt_type: string;
  success: boolean;
  response_time_ms: number;
  extracted_name: string;
  extracted_email: string;
  extracted_phone: string;
  extracted_address_line1: string | null;
  extracted_address_line2: string | null;
  email_validation_status: string;
  email_correction_suggested: string | null;
  initiated_by: string;
  created_at: string;
  original_image_url: string | null;
}

const TranscriptionLogs: React.FC = () => {
  usePageTitle('AI Transcription Logs');
  const { setPageHeader } = usePageHeader();
  const axios = useAxios();
  const { viewingAllAccounts } = useAuth();
  const [logs, setLogs] = useState<TranscriptionLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const [detailedLog, setDetailedLog] = useState<any>(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  
  const limit = 50;

  // Set up page header
  useEffect(() => {
    setPageHeader('AI Transcription Logs');
    return () => setPageHeader(null);
  }, [setPageHeader]);

  useEffect(() => {
    loadLogs();
  }, [offset]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const params: any = { limit, offset };
      
      const response = await axios.get('/api/v1/app/authenticated/admin/transcription-logs', { params });
      
      if (response.data.success) {
        setLogs(response.data.data.logs);
        setTotalCount(response.data.data.total_count);
      }
    } catch (error) {
      console.error('Failed to load transcription logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLogDetails = async (logId: number) => {
    try {
      const response = await axios.get(`/api/v1/app/authenticated/admin/transcription-logs/${logId}`);
      if (response.data.success) {
        setDetailedLog(response.data.data.log);
        setShowDetails(true);
      }
    } catch (error) {
      console.error('Failed to load log details:', error);
    }
  };


  const handleViewImage = (imageUrl: string) => {
    setSelectedImageUrl(imageUrl);
    setShowImageModal(true);
  };

  const renderJsonWithImages = (jsonData: any, depth = 0) => {
    if (!jsonData) return 'null';
    
    const indent = '  '.repeat(depth);
    
    if (Array.isArray(jsonData)) {
      return (
        <span>
          [
          {jsonData.map((item, index) => (
            <div key={index} style={{ marginLeft: `${depth * 16}px` }}>
              {indent}  {renderJsonWithImages(item, depth + 1)}{index < jsonData.length - 1 ? ',' : ''}
            </div>
          ))}
          {depth > 0 && <div style={{ marginLeft: `${(depth - 1) * 16}px` }}>{indent.slice(2)}]</div>}
          {depth === 0 && ']'}
        </span>
      );
    }
    
    if (typeof jsonData === 'object' && jsonData !== null) {
      const entries = Object.entries(jsonData);
      return (
        <span>
          {'{'}
          {entries.map(([key, value], index) => (
            <div key={key} style={{ marginLeft: `${depth * 16}px` }}>
              {indent}  "{key}": {renderJsonWithImages(value, depth + 1)}{index < entries.length - 1 ? ',' : ''}
            </div>
          ))}
          {depth > 0 && <div style={{ marginLeft: `${(depth - 1) * 16}px` }}>{indent.slice(2)}{'}'}</div>}
          {depth === 0 && '}'}
        </span>
      );
    }
    
    if (typeof jsonData === 'string') {
      // Check if this looks like an image URL
      if (jsonData.match(/^https?:\/\/.*\.(png|jpg|jpeg|gif|webp)(\?.*)?$/i)) {
        return (
          <span className="inline-flex items-center">
            <img 
              src={jsonData} 
              alt="Click to view full image" 
              className="inline-block w-24 h-24 object-cover rounded cursor-pointer hover:opacity-75 transition-opacity border-2 border-gray-300 hover:border-blue-400"
              onClick={() => handleViewImage(jsonData)}
              title="Click to view full image"
            />
          </span>
        );
      }
      return <span className="text-green-600">"{jsonData}"</span>;
    }
    
    if (typeof jsonData === 'number') {
      return <span className="text-blue-600">{jsonData}</span>;
    }
    
    if (typeof jsonData === 'boolean') {
      return <span className="text-purple-600">{jsonData.toString()}</span>;
    }
    
    return JSON.stringify(jsonData);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="container-fixed">
      {/* Logs Table */}
      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-border">
              <thead>
                <tr className="text-gray-600 fw-medium">
                  <th>Lead</th>
                  {viewingAllAccounts && <th>Account</th>}
                  <th>Service</th>
                  <th>Image</th>
                  <th>Model</th>
                  <th>Time</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td>
                      <div className="flex flex-col gap-1">
                        <div className="font-medium">{log.extracted_name || 'Unknown'}</div>
                        {log.extracted_email && (
                          <div className="text-sm text-gray-600">{log.extracted_email}</div>
                        )}
                        {log.extracted_phone && (
                          <div className="text-sm text-gray-600">{log.extracted_phone}</div>
                        )}
                        {(log.extracted_address_line1 || log.extracted_address_line2) && (
                          <div className="text-sm text-gray-600">
                            {log.extracted_address_line1}
                            {log.extracted_address_line2 && <><br />{log.extracted_address_line2}</>}
                          </div>
                        )}
                      </div>
                    </td>
                    {viewingAllAccounts && <td>{log.account_name || '-'}</td>}
                    <td>
                      {log.service_unique_identifier ? (
                        <Link 
                          to={`/services/${log.service_unique_identifier}`}
                          className="text-primary hover:text-primary-dark"
                        >
                          {log.service_name || 'View Service'}
                        </Link>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      {log.original_image_url ? (
                        <button
                          className="btn btn-sm btn-light"
                          onClick={() => handleViewImage(log.original_image_url!)}
                        >
                          <i className="ki-filled ki-picture"></i> View
                        </button>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>{log.model_used}</td>
                    <td>{log.response_time_ms}ms</td>
                    <td className="text-sm">{formatDate(log.created_at)}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-light"
                        onClick={() => loadLogDetails(log.id)}
                      >
                        Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          <div className="card-footer">
            <div className="flex items-center justify-between">
              <div className="text-gray-600 text-sm">
                Showing {offset + 1} to {Math.min(offset + limit, totalCount)} of {totalCount}
              </div>
              <div className="flex gap-2">
                <button
                  className="btn btn-sm btn-light"
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                  disabled={offset === 0}
                >
                  Previous
                </button>
                <button
                  className="btn btn-sm btn-light"
                  onClick={() => setOffset(offset + limit)}
                  disabled={offset + limit >= totalCount}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Details Modal */}
      {showDetails && detailedLog && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setShowDetails(false)}></div>
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowDetails(false)}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 border-b">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-semibold">Transcription Log Details</h3>
                  <button
                    onClick={() => setShowDetails(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <i className="ki-filled ki-cross text-2xl"></i>
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                {/* iPad Screenshot */}
                {detailedLog.original_image_url && (
                  <div className="mb-6">
                    <h4 className="font-semibold mb-3">iPad Screenshot</h4>
                    <div className="bg-gray-50 p-4 rounded flex justify-center">
                      <img 
                        src={detailedLog.original_image_url} 
                        alt="iPad screenshot" 
                        className="max-w-full max-h-64 object-contain cursor-pointer hover:opacity-75 transition-opacity"
                        onClick={() => handleViewImage(detailedLog.original_image_url!)}
                      />
                    </div>
                  </div>
                )}

                {/* Basic Info */}
                <div className="mb-6">
                  <h4 className="font-semibold mb-3">Basic Information</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-600">Signature ID:</label>
                      <p>{detailedLog.signature_unique_identifier}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Model:</label>
                      <p>{detailedLog.model_used}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Success:</label>
                      <p>{detailedLog.success ? 'Yes' : 'No'}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Response Time:</label>
                      <p>{detailedLog.response_time_ms}ms</p>
                    </div>
                  </div>
                </div>

                {/* Extracted Data */}
                <div className="mb-6">
                  <h4 className="font-semibold mb-3">Extracted Data</h4>
                  <div className="bg-gray-50 p-4 rounded">
                    <pre className="text-sm">{JSON.stringify(detailedLog.extracted_data, null, 2)}</pre>
                  </div>
                </div>

                {/* Messages (with images) */}
                {detailedLog.messages && (
                  <div className="mb-6">
                    <h4 className="font-semibold mb-3">Messages</h4>
                    <div className="bg-gray-50 p-4 rounded overflow-x-auto">
                      <div className="text-sm font-mono">
                        {renderJsonWithImages(detailedLog.messages)}
                      </div>
                    </div>
                  </div>
                )}

                {/* System Prompt */}
                {detailedLog.system_prompt && (
                  <div className="mb-6">
                    <h4 className="font-semibold mb-3">System Prompt</h4>
                    <div className="bg-gray-50 p-4 rounded">
                      <pre className="text-sm whitespace-pre-wrap">{detailedLog.system_prompt}</pre>
                    </div>
                  </div>
                )}

                {/* Raw Request */}
                {detailedLog.raw_request && (
                  <div className="mb-6">
                    <h4 className="font-semibold mb-3">Raw Request</h4>
                    <div className="bg-gray-50 p-4 rounded overflow-x-auto">
                      <div className="text-sm font-mono">
                        {renderJsonWithImages(detailedLog.raw_request)}
                      </div>
                    </div>
                  </div>
                )}

                {/* Raw Response */}
                {detailedLog.raw_response && (
                  <div className="mb-6">
                    <h4 className="font-semibold mb-3">Raw Response</h4>
                    <div className="bg-gray-50 p-4 rounded">
                      <pre className="text-sm overflow-x-auto">{JSON.stringify(detailedLog.raw_response, null, 2)}</pre>
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {detailedLog.error_message && (
                  <div className="mb-6">
                    <h4 className="font-semibold mb-3">Error Message</h4>
                    <div className="bg-red-50 p-4 rounded text-red-700">
                      {detailedLog.error_message}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Image Modal */}
      {showImageModal && selectedImageUrl && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setShowImageModal(false)}></div>
          <div className="fixed inset-0 z-50 flex items-center justify-center p-8" onClick={() => setShowImageModal(false)}>
            <div className="bg-white rounded-lg shadow-xl w-full h-full max-w-4xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="px-6 py-4 border-b flex justify-between items-center flex-shrink-0">
                <h3 className="text-lg font-semibold">iPad Signature Screenshot</h3>
                <button
                  onClick={() => setShowImageModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="ki-filled ki-cross text-xl"></i>
                </button>
              </div>
              <div className="p-6 flex-1 overflow-hidden flex items-center justify-center">
                <img 
                  src={selectedImageUrl} 
                  alt="iPad signature screenshot" 
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TranscriptionLogs;